import { inject } from '@angular/core';
import { NewsService } from '@core/services';
import { FilterQuery } from '@core/types';
import { Observable } from 'rxjs';
import { GroupEntity, GroupNewsEntity, GroupNewsList, NewsEntity, NewsList, Pagination } from '@core/entities';
import { map } from 'rxjs/operators';
import { plainToClass, plainToInstance } from 'class-transformer';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageConversionService } from '@core/services/image-conversion.service';
import { environment } from '@env/environment';
import { GenerateImageUrlWithToken } from '@core/utils';
import { SectionType } from '@core/enums';
import { PermissionService } from '@auth';
import { UseNotification } from '@core/usecases/useNotification';

export class UseNews {
  private readonly _baseUrl = environment.apiUrl;
  private readonly _newsService = inject(NewsService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _userPermissions = inject(PermissionService);
  private readonly _useNotification = new UseNotification();

  getAllNews(pagination: Partial<Pagination>, filters?: FilterQuery): Observable<NewsList> {
    const service$ = this._userPermissions.isAdmin() || this._userPermissions.isGuest() ? this._newsService.find(pagination, filters) : this._newsService.getNewsByUserId(pagination, filters);

    return service$.pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            news: this._transformNews(response.news),
          } as NewsList;
        }
        return null;
      }),
    );
  }

  getAllGroupNews(pagination: Partial<Pagination>): Observable<GroupNewsList> {
    return this._newsService.allGroupNews(pagination).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            groupNews: response.groupNews.map((item) => {
              const transformedItem = plainToClass(GroupNewsEntity, item);
              if (transformedItem.news.id && transformedItem.news.imageUrls) {
                transformedItem.news.imageUrl = GenerateImageUrlWithToken(transformedItem.news.id, SectionType.NEWS);
              }

              if (transformedItem.news?.user?.deleted) {
                transformedItem.news.user.firstName = 'Deleted';
                transformedItem.news.user.lastName = 'User';
                transformedItem.news.user.memberId = null;
              }

              return transformedItem;
            }),
          } as unknown as GroupNewsList;
        }
        return null;
      }),
    );
  }

  createNews(formData: any): Observable<any> {
    return this._newsService.create(formData);
  }

  deleteNews(newsId: number): Observable<any> {
    return this._newsService.delete(newsId);
  }

  markNewsAsRead(): Observable<any> {
    return this._newsService.markAsRead();
  }

  getNewsById(newsId: number): Observable<NewsEntity> {
    return this._newsService.get(newsId).pipe(
      map((response) => {
        if (response) {
          const transformedItem = plainToClass(NewsEntity, response);
          if (transformedItem.id && transformedItem.imageUrls) {
            transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.NEWS);
          }
          transformedItem.isHighlighted = transformedItem.isHighlighted.toString() === 'true';
          transformedItem.highlighted = transformedItem.isHighlighted.toString() === 'true';
          transformedItem.showGuestList = transformedItem.showGuestList.toString() === 'true';

          if (response.groups && response.groups.length) {
            transformedItem.groups = response.groups.map((group: any) => plainToInstance(GroupEntity, group.group));
          }

          if (response.newsTags && response.newsTags.length) {
            transformedItem.newsTags = response.newsTags.map((tag: any) => ({
              tagName: tag.tag_name,
              tagId: tag.tag_id,
            }));
          }

          if (transformedItem?.user?.deleted) {
            transformedItem.user.firstName = 'Deleted';
            transformedItem.user.lastName = 'User';
            transformedItem.user.memberId = null;
          }

          if (transformedItem?.updatedRecord) {
            transformedItem.updatedRecord = plainToInstance(NewsEntity, JSON.parse(transformedItem.updatedRecord as any)) as NewsEntity;
            transformedItem.updatedRecord.imageUrl = transformedItem.updatedRecord.newImage ? GenerateImageUrlWithToken(transformedItem.id, SectionType.UPDATE_NEWS) : null;
            if (transformedItem?.updatedRecord?.newsTags) {
              transformedItem.updatedRecord.newsTags = JSON.parse(transformedItem?.updatedRecord.newsTags as any);
              if (transformedItem?.updatedRecord?.newsTags?.length) {
                transformedItem.updatedRecord.newsTags = transformedItem?.updatedRecord.newsTags.map((tag: any) => ({
                  tagName: tag,
                  tagId: 0,
                }));
              }
            }
          }
          return transformedItem;
        }
        return null;
      }),
    );
  }

  // if( response.newsTags && response.newsTags.length) {
  //   transformedItem.newsTags = response.newsTags.map((tag: any) => plainToInstance(NewsTag, tag));
  //   transformedItem.newsTags = (response.newsTags as NewsTag[]).map((tag: NewsTag) => tag.tagName);
  // }
  updateNews(newsId: number, formData: any): Observable<any> {
    return this._newsService.update(newsId, formData);
  }

  getTopFiveNews(queryParams?: FilterQuery): Observable<NewsEntity[]> {
    return this._newsService.topFive(queryParams).pipe(
      map((response) => {
        if (response) {
          return this._transformNews(response);
        }
        return null;
      }),
    );
  }

  acceptNewsByAdmin(newsId: number): Observable<any> {
    return this._useNotification.acceptNews(newsId);
  }

  acceptNewsUpdateByAdmin(newsId: number): Observable<any> {
    return this._useNotification.acceptUpdateNews(newsId);
  }

  denyNews(newsId: number, reason: string): Observable<any> {
    return this._useNotification.denyNews(newsId, reason);
  }

  // ----------- AUX -------------

  convertBase64ToImage(image: string) {
    if (!image) {
      return null;
    }
    const base64Data = image.substring(20);
    return this._sanitizer.bypassSecurityTrustUrl(this._imageConversionService.convertBase64ToBlobUrl(base64Data)) as string;
  }

  private _transformNews(news: NewsEntity[]): NewsEntity[] {
    return news.map((item) => {
      const transformedItem = plainToClass(NewsEntity, item);
      if (transformedItem.id && transformedItem.imageUrls) {
        transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.NEWS);
      }
      transformedItem.isHighlighted = transformedItem.isHighlighted.toString() === 'true';
      transformedItem.highlighted = transformedItem.isHighlighted.toString() === 'true';
      transformedItem.showGuestList = transformedItem.showGuestList.toString() === 'true';

      if (transformedItem?.user?.deleted) {
        transformedItem.user.firstName = 'Deleted';
        transformedItem.user.lastName = 'User';
        transformedItem.user.memberId = null;
      }

      if (transformedItem.groups && transformedItem.groups.length) {
        transformedItem.groups = transformedItem.groups.map((group: any) => plainToInstance(GroupEntity, group.groups));
      }

      return transformedItem;
    });
  }
}
